// src/modulos/Cadastros/SupplierPage.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';
import { countryOptions } from '../../data/countries';
import { FiEdit, FiTrash2, FiPackage, FiX, FiMapPin, FiSearch, FiLink } from 'react-icons/fi';
import { FaMicroscope, FaClipboard } from 'react-icons/fa';

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
    TagInput,
    ComboBox,
    AddressForm,
} from '../../components/common/index.js';

import useToast from '../../hooks/useToast';
import usePagination from '../../hooks/usePagination';

const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const SUPPLIERS_COLLECTION = 'suppliers';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const ROLES_COLLECTION = 'parameters/data/cargos';

const ITEMS_PER_PAGE = 10;

const REGIME_FISCAL_OPTIONS = [
    'Lucro presumido', 'Lucro Real', 'Simples', 'Super simples', 'Pessoa física', 'Micro Empreendedor Individual',
].map(opt => ({ value: opt, label: opt }));

const TIPO_ENTREGA_OPTIONS = [
    'Na transportadora', 'À retirar', 'Nosso endereço', 'Triangular',
].map(opt => ({ value: opt, label: opt }));

const TIPO_ATIVIDADE_OPTIONS = [
    'Industrial ou Equiparado a Industrial', 'Outros',
].map(opt => ({ value: opt, label: opt }));

const ORIGEM_MERCADORIA_ICMS_OPTIONS = [
    { value: 0, label: '0 - Nacional, exceto as indicados nos códigos 3, 4, 5 e 8' },
    { value: 1, label: '1 - Estrangeira - Importação direta, exceto a indicada no código 6' },
    { value: 2, label: '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7' },
    { value: 3, label: '3 - Nacional, mercadoria ou bem com conteúdo de importação superior a 40% e inferior ou igual 70%' },
    { value: 4, label: '4 - Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos' },
    { value: 5, label: '5 - Nacional, mercadoria ou bem com conteúdo de importação inferior ou igual a 40%' },
    { value: 6, label: '6 - Estrangeira, Importação direta sem similar nacional, constante em lista CAMEX e gás natural' },
    { value: 7, label: '7 - Estrangeira, Adquirida no mercado interno s/ similar nacional, constante em lista CAMEX e gás natural' },
];

const NEW_CONTACT_MODEL = {
    name: '',
    email: '',
    cargo: '',
};

const NEW_ADDRESS_MODEL = {
    tipo: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', uf: '', cidade: '',
    contacts: [],
};

const DEFAULT_SUPPLIER_MODEL = {
    codigo: '',
    tipoPessoa: 'CNPJ',
    cnpjCpf: '',
    ie: '',
    razaoSocial: '',
    nomeFantasia: '',
    regimeFiscal: REGIME_FISCAL_OPTIONS[0].value,
    condicaoFaturamento: '',
    pais: '',
    tipoEntrega: TIPO_ENTREGA_OPTIONS[0].value,
    tipoAtividade: TIPO_ATIVIDADE_OPTIONS[0].value,
};

// New supplier relation model for adding to raw material
const NEW_SUPPLIER_RELATION_MODEL = {
    supplierId: '',
    razaoSocial: '',
    codigo: '',
    origemIcms: ORIGEM_MERCADORIA_ICMS_OPTIONS[0].value,
    unitOfMeasure: '',
    valor: '',
    pesoEspecifico: '',
    data: new Date().toISOString().split('T')[0],
    descricao: '',
    leadTime: '',
    loteMinimo: '',
};

const SupplierPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('fornecedores');
    const { toasts, addToast, removeToast } = useToast();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formStatus, setFormStatus] = useState({ message: '', type: '' });
    const [supplierData, setSupplierData] = useState(DEFAULT_SUPPLIER_MODEL);
    const [addresses, setAddresses] = useState([]);
    const [newAddress, setNewAddress] = useState(NEW_ADDRESS_MODEL);
    const [isAddingAddress, setIsAddingAddress] = useState(false);
    const [newContact, setNewContact] = useState(NEW_CONTACT_MODEL);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');

    // Raw material association state
    const [isRawMaterialSelectorOpen, setIsRawMaterialSelectorOpen] = useState(false);
    const [rawMaterialSearch, setRawMaterialSearch] = useState('');
    const [selectedRawMaterialId, setSelectedRawMaterialId] = useState('');
    const [rawMaterialRelationData, setRawMaterialRelationData] = useState(NEW_SUPPLIER_RELATION_MODEL);
    const [associatingRawMaterial, setAssociatingRawMaterial] = useState(false);

    // Flag to control when to load parameter collections (only when modal opens)
    const [shouldLoadParams, setShouldLoadParams] = useState(false);

    const {
        data: suppliers,
        mainLoading: suppliersLoading,
        formLoading,
        addDocument,
        updateDocument,
        deleteDocument
    } = useFirestore(SUPPLIERS_COLLECTION, 'codigo');

    // Only load raw materials when modal opens (for supplied materials display)
    const { data: allRawMaterials, loading: loadingRawMaterials } = useFirestore(
        shouldLoadParams ? RAW_MATERIALS_COLLECTION : null,
        'code'
    );

    // Only load product types when modal opens
    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentCollection : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.parentId : null,
        shouldLoadParams ? PRODUCT_TYPE_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    // Only load categories when modal opens
    const { data: categories, loading: loadingCategories } = useFirestore(
        shouldLoadParams ? CATEGORY_COLLECTION : null,
        'order'
    );

    // Only load billing conditions when modal opens
    const { data: billingConditions, loading: loadingBilling } = useFirestore(
        shouldLoadParams ? 'parameters/data/billing-conditions' : null,
        'name'
    );

    // Only load address types when modal opens
    const TYPE_ADDRESS_COLLECTION_PATH = {
        parentCollection: 'parameters',
        parentId: 'types',
        subCollectionName: 'tipo-endereco'
    };
    const { data: addressTypes, loading: loadingAddressTypes } = useSubCollection(
        shouldLoadParams ? TYPE_ADDRESS_COLLECTION_PATH.parentCollection : null,
        shouldLoadParams ? TYPE_ADDRESS_COLLECTION_PATH.parentId : null,
        shouldLoadParams ? TYPE_ADDRESS_COLLECTION_PATH.subCollectionName : null,
        'name'
    );

    // Only load roles when modal opens
    const { data: rolesData, loading: loadingRoles } = useFirestore(
        shouldLoadParams ? ROLES_COLLECTION : null,
        'name'
    );

    // Load units for raw material relation
    const { data: units, loading: loadingUnits } = useFirestore(
        shouldLoadParams ? 'parameters/data/unidades-medida' : null,
        'name'
    );

    const [allStates, setAllStates] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [allCities, setAllCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);

    // Ref to prevent double submission
    const isSubmitting = useRef(false);

    const fetchAddressByCep = async (cepValue) => {
        const cleanCep = cepValue.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            try {
                setLoadingStates(true);
                const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
                if (!response.ok) throw new Error(t('pages.suppliers.form.cepError'));
                const data = await response.json();
                if (!("erro" in data)) {
                    setNewAddress(prev => ({
                        ...prev,
                        endereco: data.logradouro || '',
                        bairro: data.bairro || '',
                        uf: data.uf || '',
                        cidade: data.localidade || '',
                        complemento: data.complemento || ''
                    }));
                } else {
                    addToast(t('pages.suppliers.form.cepNotFound'), 'error');
                    setNewAddress(prev => ({ ...prev, cep: '' }));
                }
            } catch (err) {
                console.error("Erro ao acessar o webservice de CEP:", err);
                addToast(t('pages.suppliers.form.cepFetchError'), 'error');
            } finally {
                setLoadingStates(false);
            }
        }
    };

    const billingOptions = useMemo(() =>
        billingConditions.map(c => ({ value: c.id, label: `${c.name} (${c.days})` }))
        , [billingConditions]);

    const typeAddressOptions = useMemo(() =>
        addressTypes.map(t => ({ value: t.id, label: t.name }))
        , [addressTypes]);

    const rolesOptions = useMemo(() =>
        rolesData.map(r => ({ value: r.id, label: r.name }))
        , [rolesData]);

    const unitOptions = useMemo(() =>
        units ? units.map(u => ({ value: u.id, label: u.name })) : []
        , [units]);

    // Get the list of raw materials already associated with this supplier
    const associatedMaterialIds = useMemo(() => {
        if (!allRawMaterials || !editingItem) return [];
        const ids = [];
        allRawMaterials.forEach(material => {
            if (material.suppliers && Array.isArray(material.suppliers)) {
                const found = material.suppliers.some(s => s.supplierId === editingItem.id);
                if (found) ids.push(material.id);
            }
        });
        return ids;
    }, [allRawMaterials, editingItem]);

    // Filter available raw materials (not yet associated)
    const availableRawMaterials = useMemo(() => {
        if (!allRawMaterials) return [];

        const lowerSearch = rawMaterialSearch.toLowerCase();

        return allRawMaterials.filter(material => {
            // Skip already associated materials
            if (associatedMaterialIds.includes(material.id)) return false;

            // Search filter
            const matchesSearch =
                (material.code && material.code.toLowerCase().includes(lowerSearch)) ||
                (material.commercialName && material.commercialName.toLowerCase().includes(lowerSearch)) ||
                (material.inciName && material.inciName.toLowerCase().includes(lowerSearch));

            return matchesSearch;
        });
    }, [allRawMaterials, associatedMaterialIds, rawMaterialSearch]);

    const suppliedRawMaterials = useMemo(() => {
        if (!editingItem || !allRawMaterials) return [];
        const materials = [];
        allRawMaterials.forEach(material => {
            if (material.suppliers && Array.isArray(material.suppliers)) {
                const supplierInfo = material.suppliers.find(s => s.supplierId === editingItem.id);
                if (supplierInfo) {
                    materials.push({ ...material, supplierSpecifics: supplierInfo });
                }
            }
        });
        return materials;
    }, [editingItem, allRawMaterials]);

    const generateNextSequentialCode = useCallback(() => {
        const prefix = 'F';
        if (!suppliers) return `${prefix}001`;
        const matchingCodes = suppliers
            .filter(item => item.codigo && item.codigo.startsWith(prefix))
            .map(item => parseInt(item.codigo.slice(prefix.length), 10))
            .filter(num => !isNaN(num));
        let nextNumber = 1;
        if (matchingCodes.length > 0) {
            nextNumber = Math.max(...matchingCodes) + 1;
        }
        return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
    }, [suppliers]);

    // Deep compare function to check if data changed
    const hasDataChanged = useCallback((current, original) => {
        if (!original) return true;

        const mainFields = ['razaoSocial', 'nomeFantasia', 'cnpjCpf', 'ie', 'tipoPessoa',
            'regimeFiscal', 'condicaoFaturamento', 'pais', 'tipoEntrega', 'tipoAtividade'];

        for (const field of mainFields) {
            if (current[field] !== original[field]) return true;
        }

        if (JSON.stringify(current.addresses || []) !== JSON.stringify(original.addresses || [])) return true;

        return false;
    }, []);

    useEffect(() => {
        if (supplierData.pais === 'BR') {
            setLoadingStates(true);
            setAllStates([]);
            setAllCities([]);
            fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
                .then(res => res.json())
                .then(data => {
                    const formattedStates = data.map(uf => ({ value: uf.sigla, label: uf.nome }));
                    setAllStates(formattedStates);
                })
                .catch(err => console.error("Erro ao buscar UFs:", err))
                .finally(() => setLoadingStates(false));
        } else {
            setAllStates([]);
            setAllCities([]);
        }
    }, [supplierData.pais]);

    useEffect(() => {
        const selectedUF = newAddress.uf;
        if (selectedUF) {
            setLoadingCities(true);
            setAllCities([]);
            fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${selectedUF}/municipios?orderBy=nome`)
                .then(res => res.json())
                .then(data => {
                    const formattedCities = data.map(city => ({ value: city.nome, label: city.nome }));
                    setAllCities(formattedCities);
                })
                .catch(err => console.error("Erro ao buscar Cidades:", err))
                .finally(() => setLoadingCities(false));
        }
    }, [newAddress.uf]);

    const resetForm = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setSupplierData(DEFAULT_SUPPLIER_MODEL);
        setAddresses([]);
        setNewAddress(NEW_ADDRESS_MODEL);
        setNewContact(NEW_CONTACT_MODEL);
        setIsAddingAddress(false);
        setFormStatus({ message: '', type: '' });
        setAllStates([]);
        setAllCities([]);
        isSubmitting.current = false;
        setShouldLoadParams(false);
    };

    const resetRawMaterialSelector = () => {
        setIsRawMaterialSelectorOpen(false);
        setRawMaterialSearch('');
        setSelectedRawMaterialId('');
        setRawMaterialRelationData(NEW_SUPPLIER_RELATION_MODEL);
        setAssociatingRawMaterial(false);
    };

    const handleNewClick = () => {
        if (!can.create) {
            addToast(t('pages.suppliers.noPermissionCreate'), 'error');
            return;
        }

        resetForm();
        setShouldLoadParams(true);
        const nextCode = generateNextSequentialCode();
        setSupplierData(prev => ({ ...prev, codigo: nextCode, pais: '' }));
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        if (!can.edit) {
            addToast(t('pages.suppliers.noPermissionEdit'), 'error');
            return;
        }

        resetForm();
        setShouldLoadParams(true);
        setEditingItem(item);
        setSupplierData(item);
        setAddresses(item.addresses || []);
        setIsModalOpen(true);
    };

    const handleDeleteClick = async (id, name) => {
        if (!can.delete) {
            addToast(t('pages.suppliers.noPermissionDelete'), 'error');
            return;
        }

        if (window.confirm(t('pages.suppliers.deleteConfirm', { name }))) {
            try {
                const result = await deleteDocument(id);
                if (result.success) {
                    addToast(t('pages.suppliers.deleteSuccess', { name }), 'success');
                } else {
                    addToast(t('pages.suppliers.deleteError'), 'error');
                }
            } catch (error) {
                addToast(t('pages.suppliers.deleteError'), 'error');
                console.error('Erro ao deletar fornecedor:', error);
            }
        }
    };

    // Handle associating a raw material with the supplier
    const handleAssociateRawMaterial = async () => {
        if (!selectedRawMaterialId || !editingItem) return;

        setAssociatingRawMaterial(true);

        try {
            const materialToUpdate = allRawMaterials.find(m => m.id === selectedRawMaterialId);
            if (!materialToUpdate) {
                addToast(t('pages.suppliers.associate.materialNotFound'), 'error');
                setAssociatingRawMaterial(false);
                return;
            }

            const currentSuppliers = materialToUpdate.suppliers || [];
            const alreadyExists = currentSuppliers.some(s => s.supplierId === editingItem.id);
            if (alreadyExists) {
                addToast(t('pages.suppliers.associate.alreadyAssociated'), 'warning');
                setAssociatingRawMaterial(false);
                resetRawMaterialSelector();
                return;
            }

            const supplierRelation = {
                supplierId: editingItem.id,
                razaoSocial: editingItem.razaoSocial,
                codigo: editingItem.codigo || '',
                origemIcms: parseInt(rawMaterialRelationData.origemIcms) || 0,
                unitOfMeasure: rawMaterialRelationData.unitOfMeasure || '',
                valor: parseFloat(String(rawMaterialRelationData.valor).replace(',', '.')) || 0,
                pesoEspecifico: parseFloat(String(rawMaterialRelationData.pesoEspecifico).replace(',', '.')) || 0,
                data: rawMaterialRelationData.data || new Date().toISOString().split('T')[0],
                descricao: rawMaterialRelationData.descricao || '',
                leadTime: parseInt(rawMaterialRelationData.leadTime) || 0,
                loteMinimo: parseFloat(String(rawMaterialRelationData.loteMinimo).replace(',', '.')) || 0,
            };

            const updatedSuppliers = [...currentSuppliers, supplierRelation];
            const result = await updateDocument(selectedRawMaterialId, {
                suppliers: updatedSuppliers,
                updatedAt: new Date()
            });

            if (result.success) {
                addToast(
                    t('pages.suppliers.associate.success', {
                        material: materialToUpdate.commercialName,
                        supplier: editingItem.razaoSocial
                    }),
                    'success'
                );
                resetRawMaterialSelector();
            } else {
                addToast(t('pages.suppliers.associate.error'), 'error');
                setAssociatingRawMaterial(false);
            }
        } catch (error) {
            console.error('Error associating raw material:', error);
            addToast(t('pages.suppliers.associate.error'), 'error');
            setAssociatingRawMaterial(false);
        }
    };

    // Handle removing a raw material from the supplier
    const handleRemoveRawMaterial = async (materialId, materialName) => {
        if (!editingItem) return;

        const confirmed = window.confirm(
            t('pages.suppliers.associate.removeConfirm', { material: materialName, supplier: editingItem.razaoSocial })
        );

        if (!confirmed) return;

        try {
            const materialToUpdate = allRawMaterials.find(m => m.id === materialId);
            if (!materialToUpdate) return;

            const currentSuppliers = materialToUpdate.suppliers || [];
            const updatedSuppliers = currentSuppliers.filter(s => s.supplierId !== editingItem.id);

            const result = await updateDocument(materialId, {
                suppliers: updatedSuppliers,
                updatedAt: new Date()
            });

            if (result.success) {
                addToast(
                    t('pages.suppliers.associate.removeSuccess', { material: materialName }),
                    'info'
                );
            } else {
                addToast(t('pages.suppliers.associate.removeError'), 'error');
            }
        } catch (error) {
            console.error('Error removing raw material:', error);
            addToast(t('pages.suppliers.associate.removeError'), 'error');
        }
    };

    // Open the raw material selector
    const handleOpenRawMaterialSelector = () => {
        if (!editingItem) {
            addToast(t('pages.suppliers.associate.saveSupplierFirst'), 'error');
            return;
        }
        setRawMaterialSearch('');
        setSelectedRawMaterialId('');
        setRawMaterialRelationData({
            ...NEW_SUPPLIER_RELATION_MODEL,
            supplierId: editingItem.id,
            razaoSocial: editingItem.razaoSocial,
            codigo: editingItem.codigo || '',
        });
        setIsRawMaterialSelectorOpen(true);
    };

    const handleFilterChange = (setter, value) => {
        setter(value);
        // Reset page is handled by usePagination
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSupplierData(prev => ({ ...prev, [name]: value }));
    };

    const handleMainSelectChange = (name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setSupplierData(prev => ({ ...prev, [name]: value }));

        if (name === 'pais' && value !== 'BR') {
            setNewAddress(prev => ({ ...prev, uf: '', cidade: '' }));
        }
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setNewAddress(prev => ({ ...prev, [name]: value }));

        if (name === 'cep') {
            const cleanCep = value.replace(/\D/g, '');
            if (cleanCep.length === 8) {
                fetchAddressByCep(cleanCep);
            }
        }
    };

    const handleAddressSelectChange = (name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setNewAddress(prev => ({ ...prev, [name]: value }));
        if (name === 'uf') {
            setNewAddress(prev => ({ ...prev, cidade: '' }));
        }
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setNewContact(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSelectChange = (name, selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        setNewContact(prev => ({ ...prev, [name]: value }));
    };

    const handleAddContact = () => {
        if (!newContact.name || !newContact.cargo) {
            addToast(t('pages.suppliers.form.contactRequired'), 'error');
            return;
        }
        setNewAddress(prev => ({
            ...prev,
            contacts: [...prev.contacts, newContact]
        }));
        setNewContact(NEW_CONTACT_MODEL);
    };

    const handleRemoveContactFromNewAddress = (index) => {
        setNewAddress(prev => ({
            ...prev,
            contacts: prev.contacts.filter((_, i) => i !== index)
        }));
    };

    const handleAddAddress = () => {
        if (!newAddress.endereco || !newAddress.cep || !newAddress.tipo) {
            addToast(t('pages.suppliers.form.addressRequired'), 'error');
            return;
        }
        setAddresses(prev => [...prev, newAddress]);
        setNewAddress(NEW_ADDRESS_MODEL);
        setNewContact(NEW_CONTACT_MODEL);
        setIsAddingAddress(false);
        setAllCities([]);
        addToast(t('pages.suppliers.form.addressAdded'), 'success');
    };

    const handleRemoveAddress = (index) => {
        setAddresses(prev => prev.filter((_, i) => i !== index));
        addToast(t('pages.suppliers.form.addressRemoved'), 'info');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting.current || formLoading) {
            return;
        }

        setFormStatus({ message: '', type: '' });

        if (!supplierData.razaoSocial || !supplierData.codigo) {
            setFormStatus({ message: t('pages.suppliers.form.requiredFields'), type: 'error' });
            return;
        }

        if (editingItem) {
            const supplierForComparison = {
                ...editingItem,
                addresses: editingItem.addresses || []
            };
            const currentDataForComparison = {
                ...supplierData,
                addresses: addresses
            };

            if (!hasDataChanged(currentDataForComparison, supplierForComparison)) {
                setFormStatus({ message: t('pages.suppliers.noChangesDetected'), type: 'info' });
                setTimeout(() => resetForm(), 1000);
                return;
            }
        }

        isSubmitting.current = true;

        const dataToSave = { ...supplierData, addresses: addresses, updatedAt: new Date() };
        let result;

        try {
            if (editingItem) {
                result = await updateDocument(editingItem.id, dataToSave);
                if (result.success) {
                    addToast(t('pages.suppliers.updateSuccess', { name: supplierData.razaoSocial }), 'success');
                    setTimeout(() => resetForm(), 1500);
                } else {
                    addToast(`${t('pages.suppliers.updateError')}: ${result.error}`, 'error');
                    isSubmitting.current = false;
                }
            } else {
                dataToSave.createdAt = new Date();
                result = await addDocument(dataToSave);
                if (result.success) {
                    addToast(t('pages.suppliers.createSuccess', { name: supplierData.razaoSocial }), 'success');
                    setEditingItem({ id: result.id, ...dataToSave });
                    setTimeout(() => resetForm(), 1500);
                } else {
                    addToast(`${t('pages.suppliers.createError')}: ${result.error}`, 'error');
                    isSubmitting.current = false;
                }
            }
        } catch (error) {
            addToast(t('pages.suppliers.createError'), 'error');
            isSubmitting.current = false;
        }
    };

    // Enhanced search with more fields
    const filteredSuppliers = useMemo(() => {
        if (!suppliers) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return suppliers.filter(supplier => {
            const matchesSearchTerm = (
                (supplier.razaoSocial && supplier.razaoSocial.toLowerCase().includes(lowerSearch)) ||
                (supplier.nomeFantasia && supplier.nomeFantasia.toLowerCase().includes(lowerSearch)) ||
                (supplier.codigo && supplier.codigo.toLowerCase().includes(lowerSearch)) ||
                (supplier.cnpjCpf && supplier.cnpjCpf.toLowerCase().includes(lowerSearch))
            );

            if (!matchesSearchTerm) return false;
            if (!filterProductTypeId && !filterCategoryId) return true;
            if (!allRawMaterials || allRawMaterials.length === 0) return false;

            return allRawMaterials.some(material => {
                const isSupplier = material.suppliers && Array.isArray(material.suppliers) &&
                    material.suppliers.some(s => s.supplierId === supplier.id);
                if (!isSupplier) return false;

                const matchesProductType = !filterProductTypeId || (
                    material.productTypeIds && material.productTypeIds.includes(filterProductTypeId)
                );
                const matchesCategory = !filterCategoryId || (
                    material.categoryIds && material.categoryIds.includes(filterCategoryId)
                );

                return matchesProductType && matchesCategory;
            });
        });
    }, [suppliers, searchTerm, filterProductTypeId, filterCategoryId, allRawMaterials]);

    const {
        currentPage,
        totalPages,
        currentItems: currentSuppliers,
        goToPage,
        resetPage,
    } = usePagination(filteredSuppliers, ITEMS_PER_PAGE);

    useEffect(() => {
        resetPage();
    }, [searchTerm, filterProductTypeId, filterCategoryId, resetPage]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
    }, []);

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '40px',
            borderColor: 'var(--border-color, #dee2e6)',
            boxShadow: 'none',
            '&:hover': { borderColor: 'var(--primary-color, #007bff)' }
        }),
        placeholder: (base) => ({ ...base, color: '#6c757d' })
    };

    // --- Modal Content ---
    const renderModalContent = () => {
        const selectedCountry = countryOptions.find(o => o.value === supplierData.pais) || null;
        const selectedRegime = REGIME_FISCAL_OPTIONS.find(o => o.value === supplierData.regimeFiscal) || null;
        const selectedBilling = billingOptions.find(o => o.value === supplierData.condicaoFaturamento) || null;
        const selectedEntrega = TIPO_ENTREGA_OPTIONS.find(o => o.value === supplierData.tipoEntrega) || null;
        const selectedAtividade = TIPO_ATIVIDADE_OPTIONS.find(o => o.value === supplierData.tipoAtividade) || null;

        const isLoading = formLoading || isSubmitting.current;

        const productTypeOptions = productTypes?.map(t => ({ id: t.id, name: t.name })) || [];
        const categoryOptions = categories?.map(c => ({ id: c.id, name: c.name })) || [];

        return (
            <>
                {/* Seção 1: Dados Principais */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaClipboard /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.suppliers.form.mainData')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.code')}</label>
                            <input
                                type="text"
                                name="codigo"
                                value={supplierData.codigo}
                                disabled
                                className={styles.input}
                            />
                            <small className={styles.fieldHint}>{t('pages.suppliers.form.codeHint')}</small>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.personType')}</label>
                            <select
                                name="tipoPessoa"
                                value={supplierData.tipoPessoa}
                                onChange={handleChange}
                                className={styles.select}
                                disabled={isLoading}
                            >
                                <option value="CNPJ">CNPJ</option>
                                <option value="CPF">CPF</option>
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{supplierData.tipoPessoa === 'CNPJ' ? 'CNPJ' : 'CPF'}</label>
                            <input
                                type="text"
                                name="cnpjCpf"
                                value={supplierData.cnpjCpf}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={supplierData.tipoPessoa === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.stateRegistration')}</label>
                            <input
                                type="text"
                                name="ie"
                                value={supplierData.ie}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.suppliers.form.stateRegistrationPlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.companyName')} <span className={styles.required}>*</span></label>
                            <input
                                type="text"
                                name="razaoSocial"
                                value={supplierData.razaoSocial}
                                onChange={handleChange}
                                required
                                className={styles.input}
                                placeholder={t('pages.suppliers.form.companyNamePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.tradeName')}</label>
                            <input
                                type="text"
                                name="nomeFantasia"
                                value={supplierData.nomeFantasia}
                                onChange={handleChange}
                                className={styles.input}
                                placeholder={t('pages.suppliers.form.tradeNamePlaceholder')}
                                disabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.taxRegime')}</label>
                            <Select
                                name="regimeFiscal"
                                styles={selectStyles}
                                options={REGIME_FISCAL_OPTIONS}
                                onChange={(option) => handleMainSelectChange('regimeFiscal', option)}
                                value={selectedRegime}
                                isDisabled={isLoading}
                                placeholder={t('common.select')}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.billingCondition')}</label>
                            <Select
                                name="condicaoFaturamento"
                                styles={selectStyles}
                                options={billingOptions}
                                onChange={(option) => handleMainSelectChange('condicaoFaturamento', option)}
                                value={selectedBilling}
                                isLoading={loadingBilling}
                                placeholder={t('common.select')}
                                isDisabled={isLoading}
                                isClearable
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.country')}</label>
                            <Select
                                name="pais"
                                styles={selectStyles}
                                options={countryOptions}
                                onChange={(option) => handleMainSelectChange('pais', option)}
                                value={selectedCountry}
                                isDisabled={isLoading}
                                placeholder={t('pages.suppliers.form.countryPlaceholder')}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.deliveryType')}</label>
                            <Select
                                name="tipoEntrega"
                                styles={selectStyles}
                                options={TIPO_ENTREGA_OPTIONS}
                                onChange={(option) => handleMainSelectChange('tipoEntrega', option)}
                                value={selectedEntrega}
                                isDisabled={isLoading}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.suppliers.form.activityType')}</label>
                            <Select
                                name="tipoAtividade"
                                styles={selectStyles}
                                options={TIPO_ATIVIDADE_OPTIONS}
                                onChange={(option) => handleMainSelectChange('tipoAtividade', option)}
                                value={selectedAtividade}
                                isDisabled={isLoading}
                            />
                        </div>
                    </div>
                </div>

                {/* Seção 2: Produtos (Placeholder) */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiPackage /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.suppliers.form.products')}</h3>
                    </div>
                    <div className={styles.comingSoonTab} style={{ padding: '30px', margin: '0 20px 20px 20px' }}>
                        <div className={styles.comingSoonIcon}>🚧</div>
                        <h4>{t('pages.suppliers.form.comingSoon')}</h4>
                        <p>{t('pages.suppliers.form.comingSoonMessage')}</p>
                    </div>
                </div>

                {/* Seção 3: Matérias-Primas Associadas */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FaMicroscope /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.suppliers.form.suppliedMaterials')}</h3>
                        <span className={styles.sectionBadge}>{suppliedRawMaterials.length}</span>
                        {can.edit && (
                            <button
                                type="button"
                                onClick={handleOpenRawMaterialSelector}
                                className={styles.addButton}
                                style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 12px' }}
                                disabled={!editingItem || isLoading}
                                title={!editingItem ? t('pages.suppliers.associate.saveSupplierFirst') : ''}
                            >
                                <FiLink size={14} style={{ marginRight: '4px' }} />
                                {t('pages.suppliers.associate.associateMaterial')}
                            </button>
                        )}
                    </div>
                    <div style={{ padding: '20px' }}>
                        {loadingRawMaterials ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <div className={styles.buttonSpinner} style={{ width: '30px', height: '30px', margin: '0 auto 15px' }}></div>
                                <p>{t('common.loading')}</p>
                            </div>
                        ) : suppliedRawMaterials.length === 0 ? (
                            <div className={styles.emptyState} style={{ padding: '40px' }}>
                                <p className={styles.emptyMessage}>
                                    {t('pages.suppliers.form.noSuppliedMaterials')}
                                    {can.edit && editingItem && (
                                        <button
                                            type="button"
                                            onClick={handleOpenRawMaterialSelector}
                                            className={styles.linkButton}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            {t('pages.suppliers.associate.associateMaterial')}
                                        </button>
                                    )}
                                </p>
                            </div>
                        ) : (
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>{t('pages.suppliers.form.materialCode')}</th>
                                            <th>{t('pages.suppliers.form.materialName')}</th>
                                            <th>{t('pages.suppliers.form.materialValue')}</th>
                                            <th>{t('pages.suppliers.form.materialDate')}</th>
                                            <th>{t('pages.suppliers.form.materialObs')}</th>
                                            <th>{t('pages.suppliers.form.materialIcmsOrigin')}</th>
                                            {can.edit && <th style={{ width: '80px' }}>{t('common.actions')}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suppliedRawMaterials.map((material) => (
                                            <tr key={material.id}>
                                                <td><strong>{material.code}</strong></td>
                                                <td>{material.commercialName}</td>
                                                <td>
                                                    {typeof material.supplierSpecifics.valor === 'number'
                                                        ? material.supplierSpecifics.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                                                        : 'N/A'}
                                                </td>
                                                <td>{material.supplierSpecifics.data || '-'}</td>
                                                <td>{material.supplierSpecifics.descricao || '-'}</td>
                                                <td>
                                                    {ORIGEM_MERCADORIA_ICMS_OPTIONS.find(opt => opt.value === material.supplierSpecifics.origemIcms)?.label || 'N/A'}
                                                </td>
                                                {can.edit && (
                                                    <td>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveRawMaterial(material.id, material.commercialName)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            title={t('pages.suppliers.associate.remove')}
                                                            disabled={isLoading}
                                                        >
                                                            <FiX size={16} />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Seção 4: Endereços */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}><FiMapPin /></div>
                        <h3 className={styles.sectionTitle}>{t('pages.suppliers.form.addresses')}</h3>
                        <span className={styles.sectionBadge}>{addresses.length}</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        {!isAddingAddress && (
                            <button
                                type="button"
                                onClick={() => setIsAddingAddress(true)}
                                className={styles.addButton}
                                disabled={isLoading}
                            >
                                + {t('pages.suppliers.form.addAddress')}
                            </button>
                        )}

                        {isAddingAddress && (
                            <AddressForm
                                address={newAddress}
                                onChange={(addr) => setNewAddress(addr)}
                                onSave={handleAddAddress}
                                onCancel={() => {
                                    setIsAddingAddress(false);
                                    setNewAddress(NEW_ADDRESS_MODEL);
                                }}
                                isSaving={isLoading}
                                addressTypes={addressTypes}
                                roles={rolesData}
                                country={supplierData.pais}
                                title={t('pages.suppliers.form.newAddress')}
                                saveText={t('pages.suppliers.form.confirmAddress')}
                                cancelText={t('common.cancel')}
                            />
                        )}

                        {addresses.length > 0 && (
                            <div className={styles.addressListContainer} style={{ marginTop: '20px' }}>
                                <h4 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>
                                    {t('pages.suppliers.form.addressesRegistered')} ({addresses.length})
                                </h4>
                                <div className={styles.addressGrid}>
                                    {addresses.map((addr, index) => (
                                        <div key={index} className={styles.addressCard}>
                                            <div className={styles.addressCardHeader}>
                                                <span className={styles.addressType}>
                                                    {typeAddressOptions.find(t => t.value === addr.tipo)?.label || 'N/A'}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveAddress(index)}
                                                    className={styles.removeAddressButton}
                                                    disabled={isLoading}
                                                >
                                                    <FiX />
                                                </button>
                                            </div>
                                            <div className={styles.addressCardContent}>
                                                <p><strong>{t('pages.suppliers.form.zipCode')}:</strong> {addr.cep}</p>
                                                <p><strong>{t('pages.suppliers.form.street')}:</strong> {addr.endereco}, {addr.numero}</p>
                                                {addr.complemento && <p><strong>{t('pages.suppliers.form.complement')}:</strong> {addr.complemento}</p>}
                                                <p><strong>{t('pages.suppliers.form.neighborhood')}:</strong> {addr.bairro}</p>
                                                <p><strong>{t('pages.suppliers.form.cityState')}:</strong> {addr.cidade} - {addr.uf}</p>
                                                {addr.contacts?.length > 0 && (
                                                    <div className={styles.addressContacts}>
                                                        <strong>{t('pages.suppliers.form.addressContacts')}:</strong>
                                                        <ul>
                                                            {addr.contacts.map((c, ci) => (
                                                                <li key={ci}>
                                                                    {c.name}
                                                                    {c.cargo && ` (${rolesOptions.find(r => r.value === c.cargo)?.label || c.cargo})`}
                                                                    {c.email && ` - ${c.email}`}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    };

    // Raw Material Selector Modal
    const renderRawMaterialSelector = () => {
        if (!isRawMaterialSelectorOpen) return null;

        const selectedMaterial = allRawMaterials?.find(m => m.id === selectedRawMaterialId);

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.formContainer} style={{ maxWidth: '700px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2>
                                <FiLink size={20} style={{ marginRight: '10px' }} />
                                {t('pages.suppliers.associate.title')}
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {t('pages.suppliers.associate.forSupplier')}: <strong>{editingItem?.razaoSocial}</strong>
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={resetRawMaterialSelector}
                            className={styles.modalCloseButton}
                            disabled={associatingRawMaterial}
                        >
                            <FiX />
                        </button>
                    </div>

                    <div className={styles.formContent}>
                        {/* Search and Select Raw Material */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiSearch /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.suppliers.associate.selectMaterial')}</h3>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.suppliers.associate.searchMaterial')}</label>
                                    <input
                                        type="text"
                                        placeholder={t('pages.suppliers.associate.searchPlaceholder')}
                                        value={rawMaterialSearch}
                                        onChange={(e) => setRawMaterialSearch(e.target.value)}
                                        className={styles.input}
                                        disabled={associatingRawMaterial}
                                    />
                                </div>

                                <div className={styles.inputGroup} style={{ marginTop: '12px' }}>
                                    <label>{t('pages.suppliers.associate.selectMaterial')}</label>
                                    <select
                                        value={selectedRawMaterialId}
                                        onChange={(e) => setSelectedRawMaterialId(e.target.value)}
                                        className={styles.select}
                                        disabled={associatingRawMaterial || loadingRawMaterials}
                                    >
                                        <option value="">{t('pages.suppliers.associate.selectMaterialPlaceholder')}</option>
                                        {availableRawMaterials.map(material => (
                                            <option key={material.id} value={material.id}>
                                                {material.code} - {material.commercialName} {material.inciName ? `(${material.inciName})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {availableRawMaterials.length === 0 && rawMaterialSearch && (
                                        <small className={styles.fieldHint} style={{ color: 'var(--warning-color)' }}>
                                            {t('pages.suppliers.associate.noMaterialsFound')}
                                        </small>
                                    )}
                                    {availableRawMaterials.length === 0 && !rawMaterialSearch && (
                                        <small className={styles.fieldHint} style={{ color: 'var(--text-secondary)' }}>
                                            {t('pages.suppliers.associate.allMaterialsAssociated')}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Supplier Relation Details */}
                        {selectedMaterial && (
                            <div className={styles.formSection}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}><FiPackage /></div>
                                    <h3 className={styles.sectionTitle}>{t('pages.suppliers.associate.supplierDetails')}</h3>
                                </div>
                                <div style={{ padding: '20px' }}>
                                    <div className={styles.formGrid}>
                                        <div className={styles.inputGroup}>
                                            <label>{t('pages.suppliers.associate.icmsOrigin')}</label>
                                            <select
                                                value={rawMaterialRelationData.origemIcms}
                                                onChange={(e) => setRawMaterialRelationData(prev => ({ ...prev, origemIcms: e.target.value }))}
                                                className={styles.select}
                                                disabled={associatingRawMaterial}
                                            >
                                                {ORIGEM_MERCADORIA_ICMS_OPTIONS.map(o => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>{t('pages.suppliers.associate.unitOfMeasure')}</label>
                                            <select
                                                value={rawMaterialRelationData.unitOfMeasure}
                                                onChange={(e) => setRawMaterialRelationData(prev => ({ ...prev, unitOfMeasure: e.target.value }))}
                                                className={styles.select}
                                                disabled={associatingRawMaterial || loadingUnits}
                                            >
                                                <option value="">{t('common.select')}</option>
                                                {unitOptions.map(u => (
                                                    <option key={u.value} value={u.value}>{u.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>{t('pages.suppliers.associate.value')}</label>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={rawMaterialRelationData.valor}
                                                onChange={(e) => setRawMaterialRelationData(prev => ({ ...prev, valor: e.target.value }))}
                                                className={styles.input}
                                                placeholder="0,00"
                                                disabled={associatingRawMaterial}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>{t('pages.suppliers.associate.date')}</label>
                                            <input
                                                type="date"
                                                value={rawMaterialRelationData.data}
                                                onChange={(e) => setRawMaterialRelationData(prev => ({ ...prev, data: e.target.value }))}
                                                className={styles.input}
                                                disabled={associatingRawMaterial}
                                            />
                                        </div>
                                        <div className={styles.inputGroup} style={{ gridColumn: '1 / -1' }}>
                                            <label>{t('pages.suppliers.associate.notes')}</label>
                                            <input
                                                type="text"
                                                value={rawMaterialRelationData.descricao}
                                                onChange={(e) => setRawMaterialRelationData(prev => ({ ...prev, descricao: e.target.value }))}
                                                className={styles.input}
                                                placeholder={t('pages.suppliers.associate.notesPlaceholder')}
                                                disabled={associatingRawMaterial}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={resetRawMaterialSelector}
                                className={styles.secondaryButton}
                                disabled={associatingRawMaterial}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleAssociateRawMaterial}
                                className={styles.primaryButton}
                                disabled={!selectedRawMaterialId || associatingRawMaterial}
                            >
                                {associatingRawMaterial ? (
                                    <>
                                        <span className={styles.buttonSpinner}></span>
                                        {t('common.saving')}
                                    </>
                                ) : (
                                    <><FiLink size={18} /> {t('pages.suppliers.associate.associate')}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // --- Table Render ---
    const renderTable = () => {
        if (suppliersLoading) {
            return <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />;
        }

        if (currentSuppliers.length === 0) {
            return (
                <EmptyState
                    icon="🏢"
                    title={searchTerm ? t('pages.suppliers.table.emptySearch') : t('pages.suppliers.table.empty')}
                    message={searchTerm ? t('pages.suppliers.search.noResults') : t('pages.suppliers.table.emptyMessage')}
                    actionText={can.create ? t('pages.suppliers.newSupplier') : undefined}
                    onAction={can.create ? handleNewClick : undefined}
                />
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{t('pages.suppliers.table.code')}</th>
                        <th>{t('pages.suppliers.table.companyName')}</th>
                        <th>{t('pages.suppliers.table.tradeName')}</th>
                        <th>{t('pages.suppliers.table.document')}</th>
                        <th style={{ width: '150px' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentSuppliers.map(item => (
                        <tr key={item.id}>
                            <td>{item.codigo}</td>
                            <td>{item.razaoSocial}</td>
                            <td>{item.nomeFantasia || '-'}</td>
                            <td>{item.cnpjCpf || '-'}</td>
                            <td>
                                <div className={styles.actionsContainer}>
                                    {can.edit && (
                                        <button
                                            onClick={() => handleEditClick(item)}
                                            className={styles.actionButton}
                                            disabled={formLoading}
                                            title={t('common.edit')}
                                        >
                                            <FiEdit />
                                        </button>
                                    )}
                                    {can.delete && (
                                        <button
                                            onClick={() => handleDeleteClick(item.id, item.razaoSocial)}
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            disabled={formLoading}
                                            title={t('common.delete')}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}
                                    {!can.edit && !can.delete && (
                                        <span className={styles.textMuted}>{t('common.noActionsAvailable')}</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast Notifications */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Supplier Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleSubmit}
                title={editingItem ? t('pages.suppliers.editSupplier') : t('pages.suppliers.newSupplier')}
                subtitle={editingItem ? `${t('pages.suppliers.editingLabel')}: ${editingItem.razaoSocial} (${editingItem.codigo})` : undefined}
                isLoading={formLoading || isSubmitting.current}
                error={formStatus.type === 'error' ? formStatus.message : ''}
                success={formStatus.type === 'success' ? formStatus.message : ''}
                submitText={editingItem ? t('common.saveChanges') : t('pages.suppliers.form.create')}
                maxWidth="900px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Raw Material Selector Modal */}
            {renderRawMaterialSelector()}

            {/* Page Header */}
            <PageHeader
                title={t('pages.suppliers.title')}
                actionText={can.create ? t('pages.suppliers.newSupplier') : undefined}
                onAction={handleNewClick}
                disabled={suppliersLoading || formLoading}
                showAction={can.create}
            />

            {/* Search and Filters */}
            <div className={styles.searchBar} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: '15px' }}>
                <div className={styles.inputGroup} style={{ flex: '1' }}>
                    <label htmlFor="listSearch">{t('pages.suppliers.search.placeholder')}</label>
                    <SearchBar
                        id="listSearch"
                        placeholder={t('pages.suppliers.search.placeholder')}
                        onSearch={handleSearch}
                        disabled={suppliersLoading}
                        debounceDelay={300}
                        initialValue={searchTerm}
                    />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                    <label htmlFor="filterType">{t('pages.suppliers.filters.type')}</label>
                    <select
                        id="filterType"
                        value={filterProductTypeId}
                        onChange={(e) => handleFilterChange(setFilterProductTypeId, e.target.value)}
                        className={`${styles.input} ${styles.select}`}
                        disabled={loadingTypes || suppliersLoading}
                    >
                        <option value="">{t('common.all')}</option>
                        {productTypes && productTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                    </select>
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                    <label htmlFor="filterCategory">{t('pages.suppliers.filters.category')}</label>
                    <select
                        id="filterCategory"
                        value={filterCategoryId}
                        onChange={(e) => handleFilterChange(setFilterCategoryId, e.target.value)}
                        className={`${styles.input} ${styles.select}`}
                        disabled={loadingCategories || suppliersLoading}
                    >
                        <option value="">{t('common.all')}</option>
                        {categories && categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer} style={{ marginTop: '20px' }}>
                {renderTable()}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredSuppliers.length}
                onPageChange={goToPage}
                disabled={suppliersLoading}
                itemsLabel={t('pages.suppliers.table.items')}
            />
        </div>
    );
};

export default SupplierPage;